import { openDB, type IDBPDatabase } from 'idb';
import { articles as seedArticles, type BlogArticle } from '@/mocks/blog';

const DB_NAME = 'clearmind-blog';
const DB_VERSION = 3;
const STORE_NAME = 'posts';
const META_STORE = 'meta';
const SEED_KEY = 'seedVersion';

let dbPromise: Promise<IDBPDatabase> | null = null;
let dbAvailable = true;

function getDB(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('slug', 'slug', { unique: true });
          store.createIndex('category', 'category');
        }
        if (!db.objectStoreNames.contains(META_STORE)) {
          db.createObjectStore(META_STORE);
        }
      },
    }).catch((err) => {
      dbPromise = null;
      dbAvailable = false;
      throw err;
    });
  }
  return dbPromise;
}

export function isBlogStoreAvailable(): boolean {
  return dbAvailable;
}

export async function initBlogStore(): Promise<void> {
  if (!dbAvailable) return;
  try {
    const db = await getDB();
    const tx = db.transaction([STORE_NAME, META_STORE], 'readwrite');
    const metaStore = tx.objectStore(META_STORE);
    const postStore = tx.objectStore(STORE_NAME);

    const storedVersion = await metaStore.get(SEED_KEY);

    if (storedVersion !== DB_VERSION) {
      const allKeys = await postStore.getAllKeys();
      for (const key of allKeys) {
        await postStore.delete(key);
      }
      for (const article of seedArticles) {
        await postStore.put(article);
      }
      await metaStore.put(DB_VERSION, SEED_KEY);
    }

    const count = await postStore.count();
    if (count === 0) {
      for (const article of seedArticles) {
        await postStore.put(article);
      }
      await metaStore.put(DB_VERSION, SEED_KEY);
    }

    await tx.done;
  } catch (err) {
    dbAvailable = false;
    dbPromise = null;
    console.warn('Blog store init failed, falling back to mock data:', err);
  }
}

export async function getAllPosts(): Promise<BlogArticle[]> {
  if (!dbAvailable) return [...seedArticles].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  try {
    const db = await getDB();
    const posts = await db.getAll(STORE_NAME);
    if (posts.length === 0) {
      return [...seedArticles].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }
    return posts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  } catch {
    dbAvailable = false;
    dbPromise = null;
    return [...seedArticles].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }
}

export async function getPostById(id: number): Promise<BlogArticle | undefined> {
  if (!dbAvailable) return seedArticles.find((a) => a.id === id);
  try {
    const db = await getDB();
    return db.get(STORE_NAME, id);
  } catch {
    dbAvailable = false;
    dbPromise = null;
    return seedArticles.find((a) => a.id === id);
  }
}

export async function getPostBySlug(slug: string): Promise<BlogArticle | undefined> {
  if (!dbAvailable) return seedArticles.find((a) => a.slug === slug);
  try {
    const db = await getDB();
    return db.getFromIndex(STORE_NAME, 'slug', slug);
  } catch {
    dbAvailable = false;
    dbPromise = null;
    return seedArticles.find((a) => a.slug === slug);
  }
}

export async function getPostsByCategory(category: string): Promise<BlogArticle[]> {
  if (!dbAvailable) {
    const filtered = category === 'all' ? seedArticles : seedArticles.filter((a) => a.category === category);
    return [...filtered].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }
  try {
    const db = await getDB();
    if (category === 'all') return getAllPosts();
    const posts = await db.getAllFromIndex(STORE_NAME, 'category', category);
    return posts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  } catch {
    dbAvailable = false;
    dbPromise = null;
    const filtered = category === 'all' ? seedArticles : seedArticles.filter((a) => a.category === category);
    return [...filtered].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }
}

export async function getRelatedPosts(article: BlogArticle, count: number = 3): Promise<BlogArticle[]> {
  const posts = await getPostsByCategory(article.category);
  return posts.filter((p) => p.id !== article.id).slice(0, count);
}

export async function getNextId(): Promise<number> {
  if (!dbAvailable) {
    const maxId = seedArticles.reduce((max, a) => Math.max(max, a.id), 0);
    return maxId + 1;
  }
  try {
    const db = await getDB();
    const all = await db.getAllKeys(STORE_NAME);
    if (all.length === 0) {
      const maxId = seedArticles.reduce((max, a) => Math.max(max, a.id), 0);
      return maxId + 1;
    }
    return Math.max(...(all as number[])) + 1;
  } catch {
    dbAvailable = false;
    dbPromise = null;
    const maxId = seedArticles.reduce((max, a) => Math.max(max, a.id), 0);
    return maxId + 1;
  }
}

export async function createPost(post: Omit<BlogArticle, 'id'>): Promise<BlogArticle> {
  if (!dbAvailable) {
    const id = seedArticles.length + 1;
    const newPost: BlogArticle = { ...post, id };
    seedArticles.push(newPost);
    return newPost;
  }
  try {
    const db = await getDB();
    const id = await getNextId();
    const newPost: BlogArticle = { ...post, id };
    await db.put(STORE_NAME, newPost);
    return newPost;
  } catch {
    dbAvailable = false;
    dbPromise = null;
    const id = seedArticles.length + 1;
    const newPost: BlogArticle = { ...post, id };
    seedArticles.push(newPost);
    return newPost;
  }
}

export async function updatePost(id: number, post: Partial<BlogArticle>): Promise<void> {
  if (!dbAvailable) {
    const idx = seedArticles.findIndex((a) => a.id === id);
    if (idx === -1) throw new Error(`Post with id ${id} not found`);
    seedArticles[idx] = { ...seedArticles[idx], ...post, id };
    return;
  }
  try {
    const db = await getDB();
    const existing = await db.get(STORE_NAME, id);
    if (!existing) throw new Error(`Post with id ${id} not found`);
    await db.put(STORE_NAME, { ...existing, ...post, id });
  } catch {
    dbAvailable = false;
    dbPromise = null;
    const idx = seedArticles.findIndex((a) => a.id === id);
    if (idx === -1) throw new Error(`Post with id ${id} not found`);
    seedArticles[idx] = { ...seedArticles[idx], ...post, id };
  }
}

export async function deletePost(id: number): Promise<void> {
  if (!dbAvailable) {
    const idx = seedArticles.findIndex((a) => a.id === id);
    if (idx !== -1) seedArticles.splice(idx, 1);
    return;
  }
  try {
    const db = await getDB();
    await db.delete(STORE_NAME, id);
  } catch {
    dbAvailable = false;
    dbPromise = null;
    const idx = seedArticles.findIndex((a) => a.id === id);
    if (idx !== -1) seedArticles.splice(idx, 1);
  }
}

export async function slugExists(slug: string, excludeId?: number): Promise<boolean> {
  if (!dbAvailable) {
    const existing = seedArticles.find((a) => a.slug === slug);
    if (!existing) return false;
    if (excludeId !== undefined && existing.id === excludeId) return false;
    return true;
  }
  try {
    const db = await getDB();
    const existing = await db.getFromIndex(STORE_NAME, 'slug', slug);
    if (!existing) return false;
    if (excludeId !== undefined && existing.id === excludeId) return false;
    return true;
  } catch {
    dbAvailable = false;
    dbPromise = null;
    const existing = seedArticles.find((a) => a.slug === slug);
    if (!existing) return false;
    if (excludeId !== undefined && existing.id === excludeId) return false;
    return true;
  }
}

export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}